import placeholderImg from '@/../img/placeholders/city-placeholder.jpg';
import ReactImageFallback from '@/utils/image-fallback-loader';
import { signS3Url } from '@/utils/sign-s3-url';
import { generateFullUrl } from '@/utils/string-utils';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ResponsiveType } from 'react-multi-carousel';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import styled from 'styled-components';
import { NoImagesBanner } from './no-images-banner';

const Div = styled.div`
  text-align: center;

  .react-multi-carousel-track {
    max-width: 1300px;
    min-width: 1000px;
    width: 100%;
  }

  img {
    /*
      Counteract Tailwind's preflight style, which sets max-width: 100% and causes the all images in the carousel
      to get "compressed" along the x-axis.
      (See also: https://tailwindcss.com/docs/preflight#images-are-constrained-to-the-parent-width)
    */
    max-width: unset;
  }
`;

const CAROUSEL_RESPONSIVITY: ResponsiveType = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 3,
    slidesToSlide: 1, // optional, default to 1.
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 2,
    slidesToSlide: 2, // optional, default to 1.
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    slidesToSlide: 1, // optional, default to 1.
  },
};

interface Props {
  imageURLs: string[];
  propertyAddress?: string;
}

export function ImageSlider(props: Props) {
  const [imageUrls, setImageUrls] = useState<string[]>([]);

  useEffect(() => {
    if (!props.imageURLs) return;

    (async function signImages() {
      const signPromises = props.imageURLs.map((url) => signS3Url(url));
      Promise.all(signPromises).then((signedImageUrls) => {
        setImageUrls(signedImageUrls);
      });
    })();
  }, [props.imageURLs]);

  const removeImageOnError = useCallback(
    (index: number) => {
      const images = [...imageUrls];
      const imagesWithoutErrors = images.filter((_, i) => i !== index);
      setImageUrls(imagesWithoutErrors);
    },
    [imageUrls]
  );

  const allImages = useMemo(() => imageUrls.map(generateFullUrl), [imageUrls]);

  if (!imageUrls || isEmpty(imageUrls)) return <NoImagesBanner />;

  const carouselAriaLabel = props.propertyAddress
    ? `Carousel of images for the property (${props.propertyAddress})`
    : 'Carousel of images for the property';

  const imageAccessibilityLabel = props.propertyAddress
    ? `View of property (${props.propertyAddress})`
    : 'View of property';

  return (
    /** Carousel component "ul" cannot be labelled. */
    <Div aria-label={carouselAriaLabel}>
      <Carousel
        arrows
        keyBoardControl // This doesn't seem to work
        partialVisible
        itemAriaLabel={imageAccessibilityLabel}
        responsive={CAROUSEL_RESPONSIVITY}
        renderButtonGroupOutside={false}
        renderDotsOutside={false}
      >
        {allImages.map((url, index) => (
          <ReactImageFallback
            alt={imageAccessibilityLabel}
            key={url}
            src={url}
            fallbackImage={placeholderImg}
            referrerPolicy="same-origin"
            style={{ height: 258 }}
            onError={() => removeImageOnError(index)}
          />
        ))}
      </Carousel>
    </Div>
  );
}
